import { useState, useEffect, useMemo, useCallback } from "react";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  fetchProtocols,
  deleteProtocol,
  toggleArchiveProtocol,
  fetchTemplates,
  deleteTemplate,
} from "./protocolsService";
import { ProtocolsHeader } from "./ProtocolsHeader";
import { ProtocolsColumns } from "./table-config";
import { Loader } from "../../shared/components/Loader";
import ProtocolDrawer from "../../shared/layout/ProtocolDrawer";
import { useAppState } from "../../lib/appContext/AppContext";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { isWorkspaceOwnerOperationAllowed } from "./permissions";
import CreateProtocolModal, {
  type CreateProtocolOption,
} from "../../shared/components/CreateProtocolModal";
import "./style.less";
import ProtocolTabs from "./ProtocolTabs";
import { SummaryInsightsPanel } from "./SummaryInsightsPanel";
import { useProtocolTemplatesTabEnabled } from "./useProtocolTemplatesTabEnabled";
import SaveTemplateModal from "../create-protocol/shared/components/SaveTemplateModal";
import { useProtocolsRouter } from "../../shared/custom-hooks/useProtocolsRouter";
import { useSyncPermissions } from "../../shared/custom-hooks/useSyncPermissions";
import { useTrackingEvent } from "../../shared/custom-hooks/useTracking";
import {
  ProtocolCreationAmplitudeEvent,
  ProtocolTemplatesAmplitudeEvent,
} from "../../shared/utils/amplitudeEvents";
import { useFeatureFlags } from "../../context/LDFlagsContext";

export const TAB_SEARCH_PARAM = "tab";
export const DEFAULT_PROTOCOLS_TAB = "active";
const PROTOCOLS_TAB_KEYS = [DEFAULT_PROTOCOLS_TAB, "archived", "templates"];

export const getTabFromSearchParams = (
  searchParams: URLSearchParams
): string => {
  const tab = searchParams.get(TAB_SEARCH_PARAM);

  return tab && PROTOCOLS_TAB_KEYS.includes(tab) ? tab : DEFAULT_PROTOCOLS_TAB;
};

export const getUpdatedPaginationForFilterChange = (
  pagination: { current: number; pageSize: number; total: number },
  key: string
): { current: number; pageSize: number; total: number } => {
  if (key !== "advancedFilters" && key !== "searchText" && key !== "status") {
    return pagination;
  }

  return {
    ...pagination,
    current: 1,
  };
};

export interface TabPagination {
  current: number;
  pageSize: number;
  total: number;
}

export const DEFAULT_PAGINATION: TabPagination = Object.freeze({
  current: 1,
  pageSize: 10,
  total: 0,
});

const DEFAULT_PAGINATION_BY_TAB: Record<string, TabPagination> =
  Object.fromEntries(
    PROTOCOLS_TAB_KEYS.map((tab) => [tab, { ...DEFAULT_PAGINATION }])
  );

// Each tab keeps its own current/pageSize/total so that paging or changing
// the page size on one tab never leaks into another tab's table.
export const getPaginationByTabForFilterChange = (
  paginationByTab: Record<string, TabPagination>,
  tab: string,
  key: string
): Record<string, TabPagination> => ({
  ...paginationByTab,
  [tab]: getUpdatedPaginationForFilterChange(
    paginationByTab[tab] ?? DEFAULT_PAGINATION,
    key
  ),
});

export interface AdvancedFilters {
  status?: string[];
  type?: string[];
  crop?: string[];
  target?: string[];
  country?: string[];
  creator?: string[];
  assignee?: string[];
}

interface BasicTabFilters {
  searchText: string;
  status: string;
}

const EMPTY_BASIC_FILTERS: BasicTabFilters = {
  searchText: "",
  status: "",
};

const DEFAULT_BASIC_FILTERS_BY_TAB: Record<string, BasicTabFilters> = {
  active: { ...EMPTY_BASIC_FILTERS },
  archived: { ...EMPTY_BASIC_FILTERS },
  templates: { ...EMPTY_BASIC_FILTERS },
};

const DEFAULT_ADVANCED_FILTERS_BY_TAB: Record<string, AdvancedFilters | null> =
  {
    active: null,
    archived: null,
    templates: null,
  };

export const Protocols = (): JSX.Element => {
  const { t } = useTranslation();
  const { router: protocolsRouter, workspaceId, orgId } = useProtocolsRouter();
  const { stateValue } = useAppState();
  const queryClient = useQueryClient();

  const [protocols, setProtocols] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateProtocolModalOpen, setIsCreateProtocolModalOpen] =
    useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedProtocolId, setSelectedProtocolId] = useState<string>("");
  const [selectedProtocolName, setSelectedProtocolName] = useState<string>("");

  const [paginationByTab, setPaginationByTab] = useState<
    Record<string, TabPagination>
  >(DEFAULT_PAGINATION_BY_TAB);

  const [searchParams, setSearchParams] = useSearchParams();

  const [filterValuesByTab, setFilterValuesByTab] = useState<
    Record<string, BasicTabFilters>
  >(DEFAULT_BASIC_FILTERS_BY_TAB);

  const [fetchedProtocolsRaw, setFetchedProtocolsRaw] = useState<any[]>([]);

  const [advancedFiltersByTab, setAdvancedFiltersByTab] = useState<
    Record<string, AdvancedFilters | null>
  >(DEFAULT_ADVANCED_FILTERS_BY_TAB);

  const protocolExtraheaders = {
    "org-id": orgId ?? "",
    "workspace-id": workspaceId ?? "",
  };

  const { data: permissions } = useSyncPermissions({ workspaceId, orgId });

  const allowed = permissions?.allowed_operations ?? [];
  const isWorkspaceOwner = isWorkspaceOwnerOperationAllowed(allowed);

  const isTemplatesTabEnabled = useProtocolTemplatesTabEnabled({
    orgId: orgId ?? "",
    workspaceId: workspaceId ?? "",
    isWorkspace: isWorkspaceOwner,
  });

  const tabFromSearchParams = getTabFromSearchParams(searchParams);

  const activeTab =
    !isTemplatesTabEnabled && tabFromSearchParams === "templates"
      ? DEFAULT_PROTOCOLS_TAB
      : tabFromSearchParams;

  const activeFilterValues =
    filterValuesByTab[activeTab] ?? EMPTY_BASIC_FILTERS;

  const activeAdvancedFilters = advancedFiltersByTab[activeTab] ?? null;

  const pagination = paginationByTab[activeTab] ?? DEFAULT_PAGINATION;

  const filters = useMemo(
    () => ({ ...activeFilterValues, tab: activeTab }),
    [activeFilterValues, activeTab]
  );

  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const { enableProtocolTemplates, enableNewFilter } = useFeatureFlags();

  const trackProtocolEvents = useTrackingEvent({
    page: "Protocols Page",
    group: "Protocols",
    module: "Protocols",
  });

  const fetchData = useCallback(
    async (
      paginationParam: { current: number; pageSize: number; total: number },
      filtersParam: { searchText: string; status: string; tab: string },
      advancedFiltersParam: AdvancedFilters | null
    ): Promise<void> => {
      try {
        setIsLoading(true);

        if (filtersParam.tab === "templates" && isTemplatesTabEnabled) {
          const templatesData = await fetchTemplates(
            orgId ?? "",
            paginationParam.current,
            paginationParam.pageSize,
            false,
            filtersParam.searchText,
            filtersParam.status,
            isWorkspaceOwner,
            workspaceId ?? "",
            advancedFiltersParam
          );

          const raw = templatesData.templates ?? [];

          setFetchedProtocolsRaw(raw);
          setProtocols(raw);

          setPaginationByTab((currentPaginationByTab) => ({
            ...currentPaginationByTab,
            [filtersParam.tab]: {
              ...(currentPaginationByTab[filtersParam.tab] ?? paginationParam),
              total: templatesData?.total ?? 0,
            },
          }));
        } else if (orgId) {
          const archived = filtersParam.tab === "archived";

          const data = await fetchProtocols(
            orgId ?? "",
            paginationParam.current,
            paginationParam.pageSize,
            archived,
            filtersParam.searchText,
            filtersParam.status,
            isWorkspaceOwner,
            workspaceId ?? "",
            advancedFiltersParam
          );

          const raw = data?.protocols ?? [];

          setFetchedProtocolsRaw(raw);
          setProtocols(raw);

          setPaginationByTab((currentPaginationByTab) => ({
            ...currentPaginationByTab,
            [filtersParam.tab]: {
              ...(currentPaginationByTab[filtersParam.tab] ?? paginationParam),
              total: data?.count ?? 0,
            },
          }));
        }
      } catch (error) {
        console.error("Error fetching protocols:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [isTemplatesTabEnabled, isWorkspaceOwner, orgId, workspaceId]
  );

  useEffect((): void => {
    void fetchData(pagination, filters, activeAdvancedFilters);
  }, [
    stateValue.orgValue,
    permissions,
    filters,
    isTemplatesTabEnabled,
    fetchData,
    pagination.current,
    pagination.pageSize,
    activeAdvancedFilters,
  ]);

  const handleArchive = async (
    protocolId: string,
    orgIdParam: string,
    workspaceIdParam: string
  ): Promise<void> => {
    try {
      await toggleArchiveProtocol(
        protocolId,
        true,
        orgIdParam,
        workspaceIdParam
      );
      await fetchData(pagination, filters, activeAdvancedFilters);
      void queryClient.invalidateQueries({ queryKey: ["summaryInsights"] });
    } catch (error) {
      setIsLoading(false);
      console.error("Archive failed:", error);
    }
  };

  const handleUnarchive = async (
    protocolId: string,
    orgIdParam: string,
    workspaceIdParam: string
  ): Promise<void> => {
    try {
      await toggleArchiveProtocol(
        protocolId,
        false,
        orgIdParam,
        workspaceIdParam
      );
      await fetchData(pagination, filters, activeAdvancedFilters);
      void queryClient.invalidateQueries({ queryKey: ["summaryInsights"] });
    } catch (error) {
      setIsLoading(false);
      console.error("Unarchive failed:", error);
    }
  };

  const handleDelete = async (
    recordId: string,
    orgIdParam: string,
    workspaceIdParam: string
  ): Promise<void> => {
    try {
      setIsLoading(true);

      if (filters.tab === "templates") {
        await deleteTemplate(recordId, orgIdParam, workspaceIdParam);
      } else {
        await deleteProtocol(recordId, orgIdParam, workspaceIdParam);
      }

      await fetchData(pagination, filters, activeAdvancedFilters);
      void queryClient.invalidateQueries({ queryKey: ["summaryInsights"] });
    } catch (error) {
      setIsLoading(false);
      console.error("Error deleting protocol:", error);
    }
  };

  const handleViewProtocol = (protocolId: string): void => {
    const protocol = protocols.find((record) => record.id === protocolId);
    void trackProtocolEvents({
      event: ProtocolCreationAmplitudeEvent.PROTOCOL_PREVIEW,
      protocol_id: protocolId,
      crop_type: protocol?.crop_type ?? protocol?.crop,
    });
    setSelectedProtocolId(protocolId);
    setDrawerVisible(true);
  };

  const handleRowClick = (protocolId: string): void => {
    handleViewProtocol(protocolId);
  };

  const handleCreateProtocol = (): void => {
    if (!enableProtocolTemplates) {
      navigateToNewProtocolFlow();
      return;
    }

    void (async () => {
      const templatesAvailable = await hasTemplatesAvailable();

      if (!templatesAvailable) {
        navigateToNewProtocolFlow();
        return;
      }

      setIsCreateProtocolModalOpen(true);
    })();
  };

  const navigateToNewProtocolFlow = (): void => {
    void trackProtocolEvents({
      event: ProtocolCreationAmplitudeEvent.PROTOCOL_CREATION_STARTED,
      creation_method: "New",
    });
    protocolsRouter.goToProtocolsCreatePage(undefined, "trial-type");
  };

  const hasTemplatesAvailable = async (): Promise<boolean> => {
    if (!isTemplatesTabEnabled || !orgId) {
      return false;
    }

    try {
      const templatesData = await fetchTemplates(
        orgId,
        1,
        1,
        false,
        "",
        "",
        isWorkspaceOwner,
        workspaceId ?? ""
      );

      return templatesData.total > 0;
    } catch (error) {
      console.error(
        "Error fetching templates for create protocol flow:",
        error
      );
      return true;
    }
  };

  const handleCreateProtocolModalCancel = (): void => {
    setIsCreateProtocolModalOpen(false);
  };

  const handleCreateProtocolModalContinue = (
    selectedOption: CreateProtocolOption,
    templateId?: string,
    templateName?: string
  ): void => {
    if (selectedOption === "newProtocol") {
      navigateToNewProtocolFlow();
    } else if (selectedOption === "template" && templateId) {
      const template = protocols.find((record) => record.id === templateId);
      void trackProtocolEvents({
        event: ProtocolCreationAmplitudeEvent.PROTOCOL_CREATION_STARTED,
        creation_method: "Template",
        template_id: templateId,
        crop_type: template?.crop_type ?? template?.crop,
      });
      void trackProtocolEvents({
        event: ProtocolTemplatesAmplitudeEvent.USE_TEMPLATE,
        originAction: "use-template",
      });

      protocolsRouter.goToProtocolsCreatePage(
        createSearchParams({
          template_id: templateId,
          ...(templateName ? { template_name: templateName } : {}),
        }).toString(),
        "trial-type"
      );
    }

    setIsCreateProtocolModalOpen(false);
  };

  const hideDrawer = (): void => {
    setSelectedProtocolId("");
    setDrawerVisible(false);
  };

  const updateFilter = (key: string, value: string): void => {
    if (key === TAB_SEARCH_PARAM) {
      setSearchParams(
        (currentSearchParams) => {
          const nextSearchParams = new URLSearchParams(currentSearchParams);
          nextSearchParams.set(TAB_SEARCH_PARAM, value);
          return nextSearchParams;
        },
        { replace: true }
      );
    } else if (key === "advancedFilters") {
      try {
        const parsed =
          typeof value === "string"
            ? (JSON.parse(value) as AdvancedFilters | null)
            : value;

        setAdvancedFiltersByTab((currentFiltersByTab) => ({
          ...currentFiltersByTab,
          [activeTab]: parsed,
        }));
      } catch (err) {
        console.warn("Failed to parse advancedFilters value:", err);
      }
    } else {
      setFilterValuesByTab((currentFiltersByTab) => ({
        ...currentFiltersByTab,
        [activeTab]: {
          ...(currentFiltersByTab[activeTab] ?? EMPTY_BASIC_FILTERS),
          [key]: value,
        },
      }));
    }

    const targetTab = key === TAB_SEARCH_PARAM ? value : activeTab;

    setPaginationByTab((currentPaginationByTab) =>
      getPaginationByTabForFilterChange(currentPaginationByTab, targetTab, key)
    );

    if (key !== TAB_SEARCH_PARAM) {
      void trackProtocolEvents({
        event: ProtocolCreationAmplitudeEvent.PROTOCOL_FILTER,
        filter_type: key,
      });
    }
  };

  const handlePagination = async (
    paginationParam: Partial<TabPagination>
  ): Promise<void> => {
    setPaginationByTab((currentPaginationByTab) => {
      const currentTabPagination =
        currentPaginationByTab[activeTab] ?? DEFAULT_PAGINATION;

      return {
        ...currentPaginationByTab,
        [activeTab]: {
          ...currentTabPagination,
          current: paginationParam?.current ?? currentTabPagination.current,
          pageSize: paginationParam?.pageSize ?? currentTabPagination.pageSize,
          total: paginationParam?.total ?? currentTabPagination.total,
        },
      };
    });
  };

  const handleCloseTemplateModal = (): void => {
    setIsTemplateModalOpen(false);
  };

  const handleOpenTemplateModal = (
    protocolId: string,
    protocolName: string
  ): void => {
    setSelectedProtocolId(protocolId);
    setSelectedProtocolName(protocolName);
    setIsTemplateModalOpen(true);
  };

  const columns = ProtocolsColumns(
    (recordId: string) => {
      void handleDelete(recordId, orgId ?? "", workspaceId ?? "");
    },
    async (protocolId: string) => {
      await handleArchive(protocolId, orgId ?? "", workspaceId ?? "");
    },
    async (protocolId: string) => {
      await handleUnarchive(protocolId, orgId ?? "", workspaceId ?? "");
    },
    filters.tab,
    handleOpenTemplateModal
  );

  return (
    <div className="protocols-dashboard-page">
      <ProtocolsHeader
        title={t("protocols.title")}
        showCreateButton
        onCreate={handleCreateProtocol}
      />

      <div className="body-content-protocol pt-0">
        {isLoading && <Loader mask message="" />}

        <div className="protocol-table-container">
          <ProtocolTabs
            protocols={protocols}
            rawProtocols={fetchedProtocolsRaw}
            columns={columns}
            pagination={pagination}
            filters={filters}
            handlePagination={handlePagination}
            handleRowClick={handleRowClick}
            updateFilter={updateFilter}
            handleUnarchive={handleUnarchive}
            permissions={permissions}
            isLoading={isLoading}
            isTemplatesTabEnabled={isTemplatesTabEnabled}
            advancedFilters={activeAdvancedFilters}
            summaryInsights={
              enableNewFilter && activeTab === DEFAULT_PROTOCOLS_TAB ? (
                <SummaryInsightsPanel
                  orgId={orgId ?? ""}
                  workspaceId={workspaceId ?? ""}
                  isWorkspace={isWorkspaceOwner}
                  archived={false}
                  search={activeFilterValues.searchText}
                  status={activeFilterValues.status}
                  advancedFilters={activeAdvancedFilters}
                />
              ) : null
            }
          />
        </div>
      </div>

      <ProtocolDrawer
        open={drawerVisible}
        onClose={hideDrawer}
        protocolId={selectedProtocolId}
        extraHeaders={protocolExtraheaders}
        showLinkedTrials
      />

      <CreateProtocolModal
        open={isCreateProtocolModalOpen}
        onCancel={handleCreateProtocolModalCancel}
        onContinue={handleCreateProtocolModalContinue}
      />

      <SaveTemplateModal
        open={isTemplateModalOpen}
        protocolId={selectedProtocolId}
        protocolName={selectedProtocolName}
        orgId={orgId ?? ""}
        workspaceId={workspaceId ?? ""}
        onClose={handleCloseTemplateModal}
      />

      <ToastContainer stacked />
    </div>
  );
};

export default Protocols;
